import { assist } from './client';

/**
 * CampusOne Assist, as this console calls it.
 *
 * ONE ENDPOINT PER OPERATION, NOT ONE PER ASSISTANT. The assist API used to
 * publish four routes for each of nineteen assistants and took the asker's
 * identity from the request body; it now has a single front door that derives the
 * caller from the access token and the institution from headers the BFF signs.
 * So there is nothing here that names a particular assistant — which assistant to
 * address is a value, and the list of values comes from the server.
 *
 * WHICH ASSISTANTS EXIST IS NOT A DECISION THIS APP MAKES. `capabilities()`
 * returns only those the signed-in person is entitled to, so the UI renders what
 * comes back rather than holding its own copy of the role rules. A second copy
 * here would drift from the server's, and the visible half of a permission model
 * drifting from the enforced half is how a product ends up offering people
 * buttons that always fail.
 */

export interface AssistantCapability {
  code: string;
  name: string;
  description: string;
}

export interface AssistConversation {
  id: string;
  assistant: string;
  assistantName: string;
  question: string;
  answer: string;
  aiProvider: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssistConversationPage {
  data: AssistConversation[];
  total: number;
  page: number;
  limit: number;
}

/** The assistants this person may address, in the order the server offers them. */
export async function capabilities(): Promise<AssistantCapability[]> {
  const body = await assist<{ assistants: AssistantCapability[] }>(
    '/assist/capabilities',
  );
  return body.assistants ?? [];
}

/** Puts one question to one assistant and returns the answered conversation. */
export function ask(
  assistant: string,
  question: string,
): Promise<AssistConversation> {
  return assist<AssistConversation>('/assist/chat', {
    method: 'POST',
    body: JSON.stringify({ assistant, question }),
  });
}

/** This person's own conversations with one assistant, newest first. */
export function conversations(
  assistant: string,
  page = 1,
  limit = 20,
): Promise<AssistConversationPage> {
  const query = new URLSearchParams({
    assistant,
    page: String(page),
    limit: String(limit),
  });
  return assist<AssistConversationPage>(`/assist/conversations?${query}`);
}

export function conversation(
  assistant: string,
  id: string,
): Promise<AssistConversation> {
  return assist<AssistConversation>(
    `/assist/conversations/${encodeURIComponent(assistant)}/${encodeURIComponent(id)}`,
  );
}

export function deleteConversation(
  assistant: string,
  id: string,
): Promise<void> {
  return assist<void>(
    `/assist/conversations/${encodeURIComponent(assistant)}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export const assistApi = {
  capabilities,
  ask,
  conversations,
  conversation,
  deleteConversation,
};
